import {
  X,
  Briefcase,
  GraduationCap,
  MapPin,
  Mail,
  Building2,
  Calendar,
  ExternalLink,
} from 'lucide-react';
import styles from '../form-submissions.module.css';

export interface LinkedInProfileData {
  name: string;
  headline: string;
  location: string;
  email: string;
  currentCompany: string;
  currentRole: string;
  linkedinUrl?: string;
  experience: {
    company: string;
    role: string;
    duration: string;
    description?: string;
  }[];
  education: {
    institution: string;
    degree: string;
    field: string;
    years: string;
  }[];
  skills: string[];
  publications?: {
    title: string;
    publisher: string;
    year: string;
  }[];
}

interface LinkedInModalProps {
  isOpen: boolean;
  onClose: () => void;
  profileData: LinkedInProfileData;
}

export function LinkedInModal({ isOpen, onClose, profileData }: LinkedInModalProps) {
  if (!isOpen) return null;

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.linkedInModal} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className={styles.linkedInModalHeader}>
          <div className={styles.linkedInTitleRow}>
            <div className={styles.linkedInIcon}>in</div>
            <h2 className={styles.linkedInModalTitle}>LinkedIn Profile</h2>
          </div>
          <button className={styles.modalCloseBtn} onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        {/* Profile Content */}
        <div className={styles.linkedInModalContent}>
          {/* Basic Info Section */}
          <div className={styles.linkedInProfileSection}>
            <div className={styles.linkedInNameRow}>
              <h3 className={styles.linkedInName}>{profileData.name}</h3>
              {/* View on LinkedIn Button */}
              {profileData.linkedinUrl && (
                <a
                  href={profileData.linkedinUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={styles.linkedInViewButton}
                >
                  <ExternalLink size={14} />
                  View on LinkedIn
                </a>
              )}
            </div>
            <p className={styles.linkedInHeadline}>{profileData.headline}</p>

            <div className={styles.linkedInInfoGrid}>
              <div className={styles.linkedInInfoItem}>
                <MapPin size={14} />
                <span>{profileData.location}</span>
              </div>
              <div className={styles.linkedInInfoItem}>
                <Mail size={14} />
                <span>{profileData.email}</span>
              </div>
              <div className={styles.linkedInInfoItem}>
                <Building2 size={14} />
                <span>{profileData.currentCompany}</span>
              </div>
              <div className={styles.linkedInInfoItem}>
                <Briefcase size={14} />
                <span>{profileData.currentRole}</span>
              </div>
            </div>
          </div>

          {/* Experience Section */}
          <div className={styles.linkedInSection}>
            <h4 className={styles.linkedInSectionTitle}>
              <Briefcase size={16} />
              Experience
            </h4>
            <div className={styles.linkedInExperienceList}>
              {profileData.experience.map((exp, index) => (
                <div key={index} className={styles.linkedInExperienceItem}>
                  <div className={styles.linkedInExpHeader}>
                    <div className={styles.linkedInExpRole}>{exp.role}</div>
                    <div className={styles.linkedInExpDuration}>
                      <Calendar size={12} />
                      {exp.duration}
                    </div>
                  </div>
                  <div className={styles.linkedInExpCompany}>{exp.company}</div>
                  {exp.description && <p className={styles.linkedInExpDesc}>{exp.description}</p>}
                </div>
              ))}
            </div>
          </div>

          {/* Education Section */}
          <div className={styles.linkedInSection}>
            <h4 className={styles.linkedInSectionTitle}>
              <GraduationCap size={16} />
              Education
            </h4>
            <div className={styles.linkedInEducationList}>
              {profileData.education.map((edu, index) => (
                <div key={index} className={styles.linkedInEducationItem}>
                  <div className={styles.linkedInEduInstitution}>{edu.institution}</div>
                  <div className={styles.linkedInEduDegree}>
                    {edu.degree} - {edu.field}
                  </div>
                  <div className={styles.linkedInEduYears}>{edu.years}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Skills Section */}
          <div className={styles.linkedInSection}>
            <h4 className={styles.linkedInSectionTitle}>Skills</h4>
            <div className={styles.linkedInSkills}>
              {profileData.skills.map((skill, index) => (
                <span key={index} className={styles.linkedInSkillTag}>
                  {skill}
                </span>
              ))}
            </div>
          </div>

          {/* Publications Section */}
          {profileData.publications && profileData.publications.length > 0 && (
            <div className={styles.linkedInSection}>
              <h4 className={styles.linkedInSectionTitle}>Publications</h4>
              <div className={styles.linkedInPublications}>
                {profileData.publications.map((pub, index) => (
                  <div key={index} className={styles.linkedInPubItem}>
                    <div className={styles.linkedInPubTitle}>{pub.title}</div>
                    <div className={styles.linkedInPubMeta}>
                      {pub.publisher} · {pub.year}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
